/**
 * Role / permission hooks — API `user.permissions` is source of truth.
 */

import { useAuthStore } from "../stores/authStore";
import {
  type FeaturePermissionKey,
  hasFeatureKey,
} from "../constants/featurePermissions";
import {
  emptyPermission,
  findModulePermission,
  getPermissions,
  hasExtraFromMatrix,
  normalizeRole,
  permissionFromMatrix,
  type Module,
  type Permission,
} from "../utils/rolePermissions";

export const useRBAC = () => {
  const { user } = useAuthStore();

  const userRole = user?.role;
  const normalizedRole = userRole ? normalizeRole(userRole) : null;
  const matrix = user?.permissions;

  const checkPermission = (
    module: Module | string,
    action: keyof Permission
  ): boolean => {
    if (normalizedRole === "PLATFORM_ADMIN") {
      return true;
    }
    // SUPER_ADMIN has full access to all company modules
    // companies create/list/delete remain platform-only
    if (normalizedRole === "SUPER_ADMIN") {
      const mod = String(module).toLowerCase();
      if (mod === "companies") {
        return action === "canRead" || action === "canUpdate";
      }
      return true;
    }
    // API matrix is the only source of truth when present
    if (matrix?.length) {
      const fromApi = permissionFromMatrix(matrix, module, action);
      return fromApi === true;
    }
    return false;
  };

  const canRead = (module: Module | string): boolean =>
    checkPermission(module, "canRead");

  const canCreate = (module: Module | string): boolean =>
    checkPermission(module, "canCreate");

  const canUpdate = (module: Module | string): boolean =>
    checkPermission(module, "canUpdate");

  const canDelete = (module: Module | string): boolean =>
    checkPermission(module, "canDelete");

  const hasExtraAction = (module: Module | string, extra: string): boolean => {
    if (normalizedRole === "PLATFORM_ADMIN") return true;
    if (normalizedRole === "SUPER_ADMIN") {
      if (module === "companies") return false;
      return true;
    }
    if (matrix?.length) {
      return hasExtraFromMatrix(matrix, module, extra);
    }
    return false;
  };

  const getModulePermissions = (module: Module | string): Permission => {
    if (normalizedRole === "PLATFORM_ADMIN" || normalizedRole === "SUPER_ADMIN") {
      return {
        canRead: true,
        canCreate: true,
        canUpdate: true,
        canDelete: true,
      };
    }
    const row = findModulePermission(matrix, module);
    if (row) {
      return {
        canRead: row.canRead,
        canCreate: row.canCreate,
        canUpdate: row.canUpdate,
        canDelete: row.canDelete,
      };
    }
    if (matrix?.length) return emptyPermission();
    return getPermissions(normalizedRole, module as Module);
  };

  const canPerformRecharge = (): boolean => {
    if (!normalizedRole) return false;
    return (
      canCreate("recharges") ||
      hasExtraAction("recharges", "direct_recharge")
    );
  };

  const hasAnyRole = (...roles: string[]): boolean => {
    if (!userRole) return false;
    const current = normalizedRole || userRole;
    return roles.some((r) => normalizeRole(r) === current || r === current);
  };

  const hasAllRoles = (...roles: string[]): boolean => {
    if (!userRole) return false;
    const current = normalizedRole || userRole;
    return roles.every((role) => normalizeRole(role) === current);
  };

  const getVisibleMenuItems = (
    allMenuItems: Array<{ path: string; module: Module | string }>
  ) => allMenuItems.filter((item) => canRead(item.module));

  const canChangePosScope = (): boolean =>
    hasAnyRole("PLATFORM_ADMIN", "SUPER_ADMIN", "ADMIN");

  const hasFeature = (key: FeaturePermissionKey): boolean => {
    if (!user) return false;
    if (normalizedRole === "PLATFORM_ADMIN") return true;
    if (hasFeatureKey(user.effectiveFeaturePermissions, key)) return true;
    if (user.featurePermissions?.[key]) return true;
    // SUPER_ADMIN always
    if (normalizedRole === "SUPER_ADMIN") return true;
    return false;
  };

  const canAssignFeaturePermissions = (): boolean =>
    hasAnyRole("PLATFORM_ADMIN", "SUPER_ADMIN", "ADMIN", "SALES_POINT_ADMIN") ||
    hasExtraAction("permissions", "manage_rbac");

  return {
    userRole: normalizedRole,
    permissions: matrix || [],
    checkPermission,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    hasExtraAction,
    getModulePermissions,
    hasAnyRole,
    hasAllRoles,
    getVisibleMenuItems,
    isAuthenticated: !!user,
    canPerformRecharge,
    canChangePosScope,
    hasFeature,
    canAssignFeaturePermissions,
  };
};
